import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {translations} from '../../../i18n/translations';
import {Alert, Grid, Skeleton, SkeletonProps, Stack} from '@mui/material';
import {Modal} from '../../modal/modal';
import {DynamicForm, DynamicFormFieldShape} from '../../dynamic-form';
import {LoadingConfigurationsType} from '../../../enums';
import {useAuth} from '../../../contexts/auth/context';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {convertArrayToIndex} from '../../../utils/functions/convert-array-to-index';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {DEFAULT_FORM_VALIDATIONS} from '../../../utils/constants';
import {ConfigurationsIndex} from '../../../types';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {CarePlanActivitiesGroup} from '../../../backend/interfaces/care-plan-activities-group';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {CarePlanConfigurationLayout} from '../../../backend/interfaces/care-plan-configuration-layout';

interface ViewActivityEntryDialogProps {
  open: boolean;
  activityGroupEntry: CarePlanActivitiesGroup;
  entry: CarePlanActivitiesGroupType;
  activitiesSectorId: string;
  repetitionType?: 'activities-group' | 'activity-type';
  onClose: () => void;
  onUpdate: (entry: CarePlanActivitiesGroup) => void;
}

const SKELETON_DEFAULT_PROPS = {
  animation: 'wave',
  variant: 'rounded',
} as SkeletonProps;

export const ViewActivityEntryDialog = ({
  open,
  onClose,
  entry,
  activityGroupEntry,
  activitiesSectorId,
  repetitionType,
}: ViewActivityEntryDialogProps) => {
  const [layout, setLayout] = useState<CarePlanConfigurationLayout | undefined>(undefined);

  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );

  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);

  const [defaultValuesIndex, setDefaultValuesIndex] = useState<any>(undefined);

  const formInstance = useForm<DynamicFormFieldShape>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const authContext = useAuth();

  const {t} = useTranslation();

  const handleModalClose = () => {
    // reset form with empty values to clear all fields
    formInstance.reset({});

    // reset state variables to clear cached values
    setLayout(undefined);
    setConfigurationsIndex(undefined);
    setDefaultValuesIndex(undefined);
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // close modal
    onClose();
  };

  const loadActivityGroupEntryTypeConfigurations = useCallback(() => {
    // Immediately reset form to prevent showing previous activity's data
    formInstance.reset({});

    // Unset previous
    setDefaultValuesIndex(undefined);

    setConfigurationsIndex(undefined);

    setLayout(undefined);

    // Start Loading Configurations
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Obtain data
    Promise.all<[Promise<ActivityType | null>, Promise<Array<CarePlanConfiguration>>]>([
      // Load Activity Type (to obtain the Configuration layout + icon + name + etc)
      EntityActivitiesRepository.instance.getType(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activityGroupEntry.activitiesGroupId,
        entry.activityTypeId
      ),

      // Load Activity Type Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activityGroupEntry.activitiesGroupId,
        entry.activityTypeId
      ),
    ])
      .then(
        ([activityType, careplanConfigurations]: [
          ActivityType | null,
          Array<CarePlanConfiguration>,
        ]) => {
          if (activityType) {
            // index activity configurations
            setConfigurationsIndex(
              convertArrayToIndex<CarePlanConfiguration>(careplanConfigurations, 'id')
            );

            // set modal layout
            setLayout(activityType.carePlanConfigurationLayout);

            // set modal default values
            setDefaultValuesIndex(
              convertArrayToIndex<any>(entry.configurations ?? [], 'carePlanConfigurationId')
            );

            // Stop Loading
            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.log(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [
    activitiesSectorId,
    activityGroupEntry.activitiesGroupId,
    authContext.state.session?.selectedAffiliation?.entity.id,
    entry.activityTypeId,
    entry.configurations,
    formInstance,
  ]);

  useEffect(() => {
    if (open) loadActivityGroupEntryTypeConfigurations();
  }, [loadActivityGroupEntryTypeConfigurations, open]);

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      preventCloseClick
      title={t(translations.activities_type_entry.modals.edit.title)}
      aria-labelledby="view-activity-group-entry-dialog-title"
      aria-describedby="view-activity-group-entry-dialog-description"
      maxWidth="sm"
      fullWidth
      readonly
      actions={[
        {
          label: t(translations.activities_type_entry.modals.view.leave),
          type: 'nondestructive',
          onClick: handleModalClose,
        },
      ]}
      actionButton={{type: 'info'}}
    >
      {loadingConfigurations === LoadingConfigurationsType.Loading && (
        <Grid container spacing={4}>
          {new Array(6).fill(1).map((_item: number, index: number) => (
            <Grid item xs={6} key={`skeleton_edit_sub_activity_${index}`}>
              <Stack gap={2} display="flex" flexDirection="column">
                <Skeleton {...SKELETON_DEFAULT_PROPS} width={120} height={20} />
                <Skeleton {...SKELETON_DEFAULT_PROPS} width="100%" height={42} />
              </Stack>
            </Grid>
          ))}
        </Grid>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Error && (
        <Alert severity="error">{t(translations.dynamic_form.error_loading)}</Alert>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded && (
        <DynamicForm
          key={`view-activity-type-entry-${entry.activityTypeId}-${open}`}
          id="view-activity-type-entry"
          activityGroupId={activityGroupEntry.activitiesGroupId}
          activityTypeId={entry.activityTypeId}
          activitiesSectorId={activitiesSectorId}
          formInstance={formInstance}
          configurationsIndex={configurationsIndex}
          layout={layout}
          onSubmit={() => {}}
          defaultValuesIndex={defaultValuesIndex}
          readonly
          configuredRepetition={repetitionType}
        />
      )}
    </Modal>
  );
};
